(function() {
  'use strict';

  const CartDrawer = {
    drawer: null,
    overlay: null,
    isOpen: false,

    init: function() {
      this.drawer = document.getElementById('cart-drawer');
      this.overlay = document.getElementById('cart-drawer-overlay');
      if (!this.drawer) return;

      requestAnimationFrame(function() {
        document.documentElement.classList.add('js-ready');
      });

      document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
      });

      document.addEventListener('keydown', (e) => {
        if (!(this.isOpen || this.drawer.classList.contains('cd--open'))) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        } else if (e.key === 'Tab') {
          // Tab out of an open frequency dropdown closes it and returns focus to its toggle.
          var openList = this.drawer.querySelector('.cd__freq-list.is-open');
          if (openList) {
            var inList = openList.contains(document.activeElement);
            openList.classList.remove('is-open');
            var tgl = openList.previousElementSibling;
            if (tgl) { tgl.setAttribute('aria-expanded', 'false'); if (inList) tgl.focus(); }
          }
          this.trapFocus(e);
        }
      });

      document.addEventListener('cart:add', async (e) => {
        // If the caller already fetched the re-rendered drawer section in its mutation
        // response (race-free), apply it directly; otherwise fall back to a GET refresh
        // (which can briefly lag right after a cart write).
        if (e && e.detail && e.detail.sectionHtml) {
          this.applyDrawerHtml(e.detail.sectionHtml);
        } else {
          await this.refresh();
        }
        // If a welcome code was entered before a subscription existed, adding a
        // subscription now completes the offer. Cheap no-op when nothing pending.
        this._maybeAddPendingWelcome();
        // Allow callers (e.g. inline quick-add) to refresh the drawer contents
        // without auto-opening it, so browsing flow isn't interrupted.
        if (e && e.detail && e.detail.silent) return;

        // If a subscription was just added, surface the recurring-charge notice
        // first (once per session). When it takes over, the cart opens only once
        // the shopper acknowledges it; otherwise we open the drawer immediately.
        if (this.detailHasSubscription(e && e.detail) &&
            window.FullSend && window.FullSend.SubNotice &&
            window.FullSend.SubNotice.maybeShow({ onConfirm: () => this.open() })) {
          return;
        }
        this.open();
      });

      this.initFrequencyToggles();

      // Reconcile the welcome offer on ANY cart mutation (cart:updated is fired by
      // applyDrawerHtml, so it covers refresh-based paths that never emit cart:add —
      // most importantly the in-drawer "switch to Subscribe" toggle (changeFrequency),
      // which is exactly what the pending-offer prompt tells shoppers to use).
      // Add the pending welcome roll once a subscription appears; remove an orphaned
      // welcome roll if its qualifying subscription is later removed.
      document.addEventListener('cart:updated', () => { this._maybeAddPendingWelcome(); this._maybeRemoveOrphanWelcome(); this._maybeRemoveOrphanFreeRoll(); });
      // On load, reconcile any pending/orphan welcome + free-can state left from a prior visit.
      this._maybeAddPendingWelcome();
      this._maybeRemoveOrphanWelcome();
      this._maybeRemoveOrphanFreeRoll();
    },

    // Best-effort: does this cart:add event describe a subscription line?
    // Handles the varied detail shapes used across the theme — a raw /cart/add.js
    // line object (detail itself), { item }, { items: [...] }, or an explicit
    // { subscription: true } flag — by looking for a selling plan or the
    // _subscription line-item property.
    detailHasSubscription: function(detail) {
      if (!detail) return false;
      if (detail.subscription === true || detail.isSubscription === true) return true;

      const lineIsSub = function(line) {
        if (!line || typeof line !== 'object') return false;
        if (line.selling_plan_allocation || line.selling_plan_id || line.selling_plan) return true;
        const p = line.properties;
        if (p) {
          if (Array.isArray(p)) {
            return p.some(function(pr) { return pr && pr[0] === '_subscription' && (pr[1] === 'true' || pr[1] === true); });
          }
          if (p._subscription === 'true' || p._subscription === true) return true;
        }
        return false;
      };

      if (Array.isArray(detail.items) && detail.items.some(lineIsSub)) return true;
      if (detail.item && lineIsSub(detail.item)) return true;
      return lineIsSub(detail);
    },

    open: function() {
      if (!this.drawer) return;
      this._lastFocus = document.activeElement;
      this.isOpen = true;
      this.drawer.classList.add('cd--open');
      this.overlay.classList.add('cd__overlay--visible');
      document.body.style.overflow = 'hidden';
      const btn = this.drawer.querySelector('button');
      if (btn) btn.focus();
    },

    close: function() {
      if (!this.drawer) return;
      this.isOpen = false;
      this.drawer.classList.remove('cd--open');
      this.overlay.classList.remove('cd__overlay--visible');
      document.body.style.overflow = '';
      // Return focus to whatever opened the drawer (e.g. the cart toggle).
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        this._lastFocus.focus();
      }
      this._lastFocus = null;
    },

    // Visible, focusable elements inside the drawer (for the focus trap).
    getFocusable: function() {
      if (!this.drawer) return [];
      var sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.prototype.filter.call(this.drawer.querySelectorAll(sel), function(el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
    },

    // Keep Tab focus within the open dialog (aria-modal), wrapping at the ends.
    trapFocus: function(e) {
      var list = this.getFocusable();
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1], active = document.activeElement;
      if (!this.drawer.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    },

    toggle: function() { this.isOpen ? this.close() : this.open(); },

    updateQuantity: async function(key, quantity) {
      // Immediate feedback: dim this line's stepper while the change is in flight.
      const qtyEl = this.drawer && this.drawer.querySelector('[data-line-item-key="' + key + '"] .cd__qty');
      if (qtyEl) qtyEl.classList.add('is-loading');
      try {
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        if (r.ok) {
          await this.refresh();
        } else {
          const data = await r.json().catch(() => null);
          const msg = data && data.description ? data.description : 'Could not update quantity.';
          await this.refresh();
          this.showNotice(msg);
        }
      } catch (e) {
        console.error('Cart update failed:', e);
        if (qtyEl) qtyEl.classList.remove('is-loading');
      }
    },

    showNotice: function(msg) {
      let notice = document.querySelector('.cd__notice');
      if (!notice) {
        notice = document.createElement('div');
        notice.className = 'cd__notice';
        const body = document.querySelector('[data-cart-body]');
        if (body) body.prepend(notice);
      }
      notice.textContent = msg;
      notice.classList.add('cd__notice--visible');
      clearTimeout(this._noticeTimer);
      this._noticeTimer = setTimeout(() => notice.classList.remove('cd__notice--visible'), 4000);
    },

    removeItem: async function(key) { await this.updateQuantity(key, 0); },

    removeBundle: async function(...keys) {
      try {
        const updates = {};
        keys.forEach(k => { updates[k] = 0; });
        const r = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        if (r.ok) this.refresh();
      } catch (e) { console.error('Bundle remove failed:', e); }
    },

    addItem: async function(variantId, quantity = 1) {
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity })
        });
        if (r.ok) { this.refresh(); }
      } catch (e) { console.error('Add item failed:', e); }
    },

    // ── Upsell rail ──
    // Markup lives in snippets/cart-drawer-upsells.liquid (rendered by both
    // drawer files). Per-card state is DOM-held: the selected strength sits in
    // data-selected-strength and the variant matrix (id / pre-formatted price /
    // availability per strength) in a JSON island, both regenerated server-side
    // on every drawer swap — so selection resets to the default after any cart
    // mutation, and a just-added product drops out of the rail automatically.
    _upsellData: function(card) {
      const el = card.querySelector('[data-upsell-data]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    },

    selectUpsellStrength: function(pill) {
      const card = pill.closest('[data-upsell-card]');
      if (!card || pill.disabled) return;
      const data = this._upsellData(card);
      if (!data) return;
      const v = data.variants[pill.dataset.strength];
      if (!v) return;
      card.querySelectorAll('[data-upsell-pill]').forEach(p => {
        const on = p === pill;
        p.classList.toggle('is-selected', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      card.dataset.selectedStrength = pill.dataset.strength;
      const priceEl = card.querySelector('[data-upsell-price]');
      if (priceEl) priceEl.textContent = v.priceLabel;
      const addBtn = card.querySelector('[data-upsell-add]');
      if (addBtn) addBtn.disabled = !v.available;
    },

    addUpsell: async function(btn) {
      const card = btn.closest('[data-upsell-card]');
      if (!card || btn.classList.contains('is-loading')) return;
      const data = this._upsellData(card);
      if (!data) return;
      const v = data.variants[card.dataset.selectedStrength];
      if (!v || !v.available) return;
      const item = { id: v.id, quantity: 1 };
      if (data.planId) item.selling_plan = data.planId;
      btn.classList.add('is-loading');
      btn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [item],
            sections: ['cart-drawer'], sections_url: window.location.pathname
          })
        });
        if (!res.ok) {
          const d = await res.json().catch(() => null);
          this.showNotice(d && d.description ? d.description : 'Could not add that item.');
          btn.classList.remove('is-loading');
          btn.disabled = false;
          return;
        }
        const payload = await res.json().catch(() => null);
        if (payload && payload.sections && payload.sections['cart-drawer']) {
          this.applyDrawerHtml(payload.sections['cart-drawer']);
        } else {
          await this.refresh();
        }
        // The drawer is already open, so no this.open() (it would re-steal
        // focus), but a subscription add still owes the shopper the
        // once-per-session recurring-charge notice.
        if (item.selling_plan && window.FullSend && window.FullSend.SubNotice) {
          window.FullSend.SubNotice.maybeShow({});
        }
      } catch (e) {
        console.error('Upsell add failed:', e);
        this.showNotice('Something went wrong. Please try again.');
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    },

    // ── Coupon / promo code ──
    // Controls use inline on* handlers (the drawer's convention) so they keep
    // working after refresh() swaps the footer innerHTML.
    toggleCoupon: function(btn) {
      const wrap = btn.closest('[data-coupon]');
      if (!wrap) return;
      const form = wrap.querySelector('[data-coupon-form]');
      if (!form) return;
      const open = form.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        const input = form.querySelector('[data-coupon-input]');
        if (input) setTimeout(function() { input.focus(); }, 60);
      }
    },

    _setCouponMsg: function(wrap, text, type) {
      if (!wrap) return;
      const msg = wrap.querySelector('[data-coupon-msg]');
      if (!msg) return;
      msg.textContent = text || '';
      msg.classList.remove('cd__coupon-msg--error', 'cd__coupon-msg--success');
      if (type) msg.classList.add('cd__coupon-msg--' + type);
    },

    _resetCouponBtn: function(btn) {
      if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
    },

    // Total item-level discount currently on the cart (used to detect whether a
    // generic code actually applied — the storefront has no code-validation API).
    _cartDiscountTotal: async function() {
      try {
        const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!r.ok) return 0;
        const c = await r.json();
        return c.total_discount || 0;
      } catch (e) { return 0; }
    },

    _hasCouponLine: async function(codes) {
      try {
        const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!r.ok) return false;
        const c = await r.json();
        const list = (Array.isArray(codes) ? codes : [codes]).map(function(s) { return String(s || '').toUpperCase(); }).filter(Boolean);
        return (c.items || []).some(function(it) {
          const p = it.properties || {};
          return p._coupon && list.indexOf(String(p._coupon).toUpperCase()) !== -1;
        });
      } catch (e) { return false; }
    },

    // ── Welcome subscription offer (code ROLL) ──────────────────────────────
    // True if the cart holds a subscription line that is NOT the welcome roll
    // itself (the welcome roll is one-time, so this normally just means "any
    // subscription line"). Drives the gate before adding the free roll.
    _cartHasSubscription: async function() {
      try {
        const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!r.ok) return false;
        const c = await r.json();
        return (c.items || []).some(function(it) {
          if (!it.selling_plan_allocation) return false;
          const p = it.properties || {};
          return !(p._welcome === 'true' || p._welcome === true);
        });
      } catch (e) { return false; }
    },

    // True if a welcome free-roll line is already in the cart (limit 1).
    _hasWelcomeLine: async function() {
      try {
        const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!r.ok) return false;
        const c = await r.json();
        return (c.items || []).some(function(it) {
          const p = it.properties || {};
          return p._welcome === 'true' || p._welcome === true;
        });
      } catch (e) { return false; }
    },

    // Add the configured welcome roll as a one-time $0 line (no selling plan).
    // Reads the resolved variant from the current [data-coupon] element so it
    // works after AJAX refreshes replace the footer. Returns true on success.
    _addWelcomeRoll: async function(code) {
      const wrap = document.querySelector('[data-coupon]');
      if (!wrap) return false;
      const variantId = wrap.dataset.welcomeVariantId;
      if (!variantId || wrap.dataset.welcomeAvailable !== 'true') return false;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: 1,
              properties: { _welcome: 'true', _coupon: String(code || '').toUpperCase() } }],
            sections: ['cart-drawer'], sections_url: window.location.pathname
          })
        });
        if (!res.ok) return false;
        const data = await res.json().catch(function() { return null; });
        if (data && data.sections && data.sections['cart-drawer']) this.applyDrawerHtml(data.sections['cart-drawer']);
        else await this.refresh();
        return true;
      } catch (e) { console.error('Welcome roll add failed:', e); return false; }
    },

    // If a welcome code is pending (entered before a subscription existed) and a
    // subscription now exists, add the free roll and clear the flag. Hooked to
    // the cart:add event. Guarded against re-entry.
    _maybeAddPendingWelcome: async function() {
      if (this._welcomeInFlight) return;
      let code = null;
      try { code = localStorage.getItem('fs_pending_welcome'); } catch (e) {}
      if (!code) return;
      const wrap = document.querySelector('[data-coupon]');
      if (!wrap || !wrap.dataset.welcomeVariantId || wrap.dataset.welcomeAvailable !== 'true') return;
      this._welcomeInFlight = true;
      try {
        if (await this._hasWelcomeLine()) { try { localStorage.removeItem('fs_pending_welcome'); } catch (e) {} return; }
        if (!(await this._cartHasSubscription())) return; // still no sub — keep pending
        const added = await this._addWelcomeRoll(code);
        if (added) { try { localStorage.removeItem('fs_pending_welcome'); } catch (e) {} }
      } finally { this._welcomeInFlight = false; }
    },

    // If a welcome roll is in the cart but its qualifying subscription is gone,
    // remove the roll (the BXGY discount would no longer zero it) and re-arm the
    // pending flag so it returns if the shopper re-subscribes. Hooked to
    // cart:updated. Guarded against re-entry.
    _maybeRemoveOrphanWelcome: async function() {
      if (this._orphanInFlight) return;
      this._orphanInFlight = true;
      try {
        const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!r.ok) return;
        const c = await r.json();
        const items = c.items || [];
        const welcome = items.filter(function(it) {
          const p = it.properties || {};
          return p._welcome === 'true' || p._welcome === true;
        });
        if (!welcome.length) return;
        const hasSub = items.some(function(it) {
          if (!it.selling_plan_allocation) return false;
          const p = it.properties || {};
          return !(p._welcome === 'true' || p._welcome === true);
        });
        if (hasSub) return;
        const updates = {};
        let code = null;
        welcome.forEach(function(it) { updates[it.key] = 0; const p = it.properties || {}; if (p._coupon) code = p._coupon; });
        if (code) { try { localStorage.setItem('fs_pending_welcome', code); } catch (e) {} }
        const ur = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates, sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
        if (ur.ok) {
          const d = await ur.json().catch(function() { return null; });
          if (d && d.sections && d.sections['cart-drawer']) this.applyDrawerHtml(d.sections['cart-drawer']);
        }
      } catch (e) { console.error('Orphan welcome check failed:', e); }
      finally { this._orphanInFlight = false; }
    },

    // If a free-can coupon line (NELK-style: any configured coupon_free_roll_code)
    // is in the cart but there are no paid items left to accompany it, remove it —
    // a gift-with-purchase can't stand alone. Mirrors _maybeRemoveOrphanWelcome but
    // keyed on "no paid items" instead of "no subscription", and does NOT re-add
    // (the shopper re-enters the code). Hooked to cart:updated. Guarded re-entry.
    _maybeRemoveOrphanFreeRoll: async function() {
      if (this._orphanFreeRollInFlight) return;
      this._orphanFreeRollInFlight = true;
      try {
        const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!r.ok) return;
        const c = await r.json();
        const items = c.items || [];
        // The full configured free-can code list, as emitted on [data-coupon].
        const wrap = document.querySelector('[data-coupon]');
        const codes = ((wrap && wrap.dataset.couponCode) || '').split(',')
          .map(function(s) { return s.trim().toUpperCase(); }).filter(Boolean);
        // A free-can line = _free_roll + a _coupon matching a configured code.
        // (The welcome roll uses _welcome; the bundle builder's free roll and the
        // tracker redeem carry no _coupon — so neither is touched here.)
        const isFreeCan = function(it) {
          const p = it.properties || {};
          if (!(p._free_roll === 'true' || p._free_roll === true)) return false;
          const cp = p._coupon ? String(p._coupon).toUpperCase() : '';
          if (!cp) return false;
          return codes.length ? codes.indexOf(cp) !== -1 : true;
        };
        const freeCans = items.filter(isFreeCan);
        if (!freeCans.length) return;
        // "Paid" = any line that is not itself a free gift (no _free_roll, no _welcome).
        const hasPaid = items.some(function(it) {
          const p = it.properties || {};
          const isFreeGift = (p._free_roll === 'true' || p._free_roll === true) ||
                             (p._welcome === 'true' || p._welcome === true);
          return !isFreeGift;
        });
        if (hasPaid) return;
        const updates = {};
        freeCans.forEach(function(it) { updates[it.key] = 0; });
        const ur = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates, sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
        if (ur.ok) {
          const d = await ur.json().catch(function() { return null; });
          if (d && d.sections && d.sections['cart-drawer']) this.applyDrawerHtml(d.sections['cart-drawer']);
        }
      } catch (e) { console.error('Orphan free-can check failed:', e); }
      finally { this._orphanFreeRollInFlight = false; }
    },

    applyCoupon: async function(event) {
      if (event && event.preventDefault) event.preventDefault();
      const form = event && event.target ? event.target : null;
      if (!form) return false;
      const wrap = form.closest('[data-coupon]');
      const input = form.querySelector('[data-coupon-input]');
      const applyBtn = form.querySelector('[data-coupon-apply]');
      if (!wrap || !input) return false;

      const raw = (input.value || '').trim();
      if (!raw) { this._setCouponMsg(wrap, 'Enter a code first.', 'error'); input.focus(); return false; }
      const code = raw.toUpperCase();
      const freeCodes = (wrap.dataset.couponCode || '').split(',').map(function(s) { return s.trim().toUpperCase(); }).filter(Boolean);
      const isFreeRoll = freeCodes.indexOf(code) !== -1;
      const welcomeCodes = (wrap.dataset.welcomeCodes || '').split(',').map(function(s) { return s.trim().toUpperCase(); }).filter(Boolean);
      const flavorCodes = (wrap.dataset.flavorCodes || '').split(',').map(function(s) { return s.trim().toUpperCase(); }).filter(Boolean);
      const isWelcome = welcomeCodes.indexOf(code) !== -1 || flavorCodes.indexOf(code) !== -1;
      const isFlavor = false;

      if (applyBtn) { applyBtn.classList.add('is-loading'); applyBtn.disabled = true; }
      this._setCouponMsg(wrap, '', null);

      try {
        // 1) Apply the Shopify discount code via the native shareable link so it
        //    persists to checkout. For generic codes, sample the discount total
        //    before/after to tell whether it actually took.
        let discountApplied = false;
        if (isFreeRoll || isWelcome || isFlavor) {
          try { await fetch('/discount/' + encodeURIComponent(raw), { credentials: 'same-origin' }); } catch (e) {}
        } else {
          const before = await this._cartDiscountTotal();
          try { await fetch('/discount/' + encodeURIComponent(raw), { credentials: 'same-origin' }); } catch (e) {}
          const after = await this._cartDiscountTotal();
          discountApplied = after > before;
        }

        // 2a) Welcome code (e.g. ROLL): only add the free roll if the cart is a
        //     subscription order. Otherwise remember the code and add it later
        //     (see _maybeAddPendingWelcome on the cart:add event).
        if (isWelcome) {
          const wVariant = wrap.dataset.welcomeVariantId;
          const wAvailable = wrap.dataset.welcomeAvailable === 'true';
          if (!wVariant || !wAvailable) {
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'This offer is currently unavailable.', 'error');
            return false;
          }
          if (await this._hasWelcomeLine()) {
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'You already have your free roll (limit 1).', 'success');
            return false;
          }
          if (!(await this._cartHasSubscription())) {
            try { localStorage.setItem('fs_pending_welcome', code); } catch (e) {}
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'Add a subscription to claim your free roll — switch any product to Subscribe & Save.', null);
            return false;
          }
          const added = await this._addWelcomeRoll(code);
          if (added) {
            try { localStorage.removeItem('fs_pending_welcome'); } catch (e) {}
            this.open();
          } else {
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'Could not add the free roll.', 'error');
          }
          return false;
        }

        // 2b) Flavor subscription code (dynamic strength offer)
        if (isFlavor) {
          const fVariant = wrap.dataset.flavorVariantId;
          const fAvailable = wrap.dataset.flavorAvailable === 'true';
          if (!fVariant || !fAvailable) {
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'This offer is currently unavailable.', 'error');
            return false;
          }
          if (await this._hasWelcomeLine()) {
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'You already have your free offer item (limit 1).', 'success');
            return false;
          }
          if (!(await this._cartHasSubscription())) {
            try { localStorage.setItem('fs_pending_welcome', code); } catch (e) {}
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'Add a subscription to claim your free offer — switch any product to Subscribe & Save.', null);
            return false;
          }
          const added = await this._addWelcomeRoll(code);
          if (added) {
            try { localStorage.removeItem('fs_pending_welcome'); } catch (e) {}
            this.open();
          } else {
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'Could not add the free item.', 'error');
          }
          return false;
        }
        // 2) The configured code (e.g. NELK) also drops in the free roll as a $0 line.
        if (isFreeRoll) {
          const variantId = wrap.dataset.freeVariantId;
          const available = wrap.dataset.freeAvailable === 'true';
          if (!variantId || !available) {
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'This offer is currently unavailable.', 'error');
            return false;
          }
          if (await this._hasCouponLine(freeCodes)) {
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, 'You already have your free item (limit 1).', 'success');
            return false;
          }
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { _free_roll: 'true', _coupon: code } }],
              sections: ['cart-drawer'], sections_url: window.location.pathname
            })
          });
          if (res.ok) {
            const data = await res.json().catch(function() { return null; });
            if (data && data.sections && data.sections['cart-drawer']) this.applyDrawerHtml(data.sections['cart-drawer']);
            else await this.refresh();
            this.open();
          } else {
            const d = await res.json().catch(function() { return null; });
            this._resetCouponBtn(applyBtn);
            this._setCouponMsg(wrap, (d && d.description) ? d.description : 'Could not add the free can.', 'error');
          }
          return false;
        }

        // 3) Generic code — verify if discount changed subtotal or applied to cart items
        await this.refresh();
        const newWrap = document.querySelector('[data-coupon]');
        if (newWrap) {
          const newForm = newWrap.querySelector('[data-coupon-form]');
          if (newForm) newForm.classList.add('is-open');
          const tgl = newWrap.querySelector('[data-coupon-toggle]');
          if (tgl) tgl.setAttribute('aria-expanded', 'true');

          if (discountApplied) {
            this._setCouponMsg(newWrap, '“' + code + '” applied successfully!', 'success');
          } else {
            this._setCouponMsg(newWrap, '“' + code + '” is invalid or does not apply to this order.', 'error');
          }
        }
        return false;
      } catch (e) {
        console.error('Coupon apply failed:', e);
        this._resetCouponBtn(applyBtn);
        this._setCouponMsg(wrap, 'Something went wrong. Please try again.', 'error');
        return false;
      }
    },

    removeCoupon: async function(code, keysCsv) {
      const keys = (keysCsv || '').split(',').filter(Boolean);
      if (!keys.length) { await this.refresh(); return; }
      try {
        const updates = {};
        keys.forEach(function(k) { updates[k] = 0; });
        const r = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates, sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
        if (r.ok) {
          const data = await r.json().catch(function() { return null; });
          if (data && data.sections && data.sections['cart-drawer']) this.applyDrawerHtml(data.sections['cart-drawer']);
          else await this.refresh();
        } else { await this.refresh(); }
      } catch (e) { console.error('Coupon remove failed:', e); await this.refresh(); }
    },

    changeFrequency: async function(itemKey, quantity, variantId, sellingPlanId, properties) {
      try {
        // Build add payload first so we can restore on failure
        const payload = {
          items: [{
            id: parseInt(variantId, 10),
            quantity: quantity,
            selling_plan: parseInt(sellingPlanId, 10)
          }]
        };
        if (properties && typeof properties === 'object' && Object.keys(properties).length > 0) {
          payload.items[0].properties = properties;
        }

        // Remove old line item
        const removeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: itemKey, quantity: 0 })
        });
        if (!removeRes.ok) {
          console.error('Frequency change: remove failed', await removeRes.text());
          await this.refresh();
          return;
        }

        // Re-add with new selling plan
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!addRes.ok) {
          console.error('Frequency change: add failed', await addRes.text());
        }

        await this.refresh();
      } catch (e) { console.error('Frequency change failed:', e); await this.refresh(); }
    },

    initFrequencyToggles: function() {
      const drawer = this.drawer;
      if (!drawer) return;

      const closeAllLists = () => {
        drawer.querySelectorAll('.cd__freq-list.is-open').forEach(l => {
          l.classList.remove('is-open');
          if (l.previousElementSibling) l.previousElementSibling.setAttribute('aria-expanded', 'false');
        });
      };

      drawer.querySelectorAll('[data-freq-toggle]').forEach(btn => {
        const list = btn.parentElement.querySelector('[data-freq-list]');
        if (!list) return;
        const options = () => Array.prototype.slice.call(list.querySelectorAll('.cd__freq-option'));
        const openAndFocus = () => {
          const wasOpen = list.classList.contains('is-open');
          closeAllLists();
          if (!wasOpen) {
            list.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
          }
          const sel = list.querySelector('.cd__freq-option.is-selected') || options()[0];
          if (sel) sel.focus();
        };

        // Toggle open/close (mouse, and Enter/Space via the native button)
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (list.classList.contains('is-open')) { closeAllLists(); }
          else { openAndFocus(); }
        });
        // ArrowDown opens the listbox and moves focus into it
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); openAndFocus(); }
        });

        // Option activation — change selling plan
        options().forEach(opt => {
          opt.addEventListener('click', async (e) => {
            e.stopPropagation();
            if (opt.classList.contains('is-selected') || opt.classList.contains('is-loading')) return;
            const itemKey = list.dataset.itemKey;
            const qty = parseInt(list.dataset.itemQty, 10);
            const variantId = opt.dataset.variantId;
            const planId = opt.dataset.planId;
            let properties = {};
            try {
              const raw = JSON.parse(list.dataset.itemProperties || '{}');
              // Liquid outputs properties as [["key","val"],...] — convert to hash
              if (Array.isArray(raw)) { raw.forEach(function(pair) { properties[pair[0]] = pair[1]; }); }
              else { properties = raw; }
            } catch (_) {}
            opt.classList.add('is-loading');
            await this.changeFrequency(itemKey, qty, variantId, planId, properties);
          });
        });

        // Roving keyboard navigation within the listbox (WAI-ARIA listbox pattern)
        list.addEventListener('keydown', (e) => {
          const opts = options();
          if (!opts.length) return;
          const i = opts.indexOf(document.activeElement);
          if (e.key === 'ArrowDown') { e.preventDefault(); (opts[i + 1] || opts[0]).focus(); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); (opts[i - 1] || opts[opts.length - 1]).focus(); }
          else if (e.key === 'Home') { e.preventDefault(); opts[0].focus(); }
          else if (e.key === 'End') { e.preventDefault(); opts[opts.length - 1].focus(); }
          else if (e.key === 'Enter' || e.key === ' ') {
            if (document.activeElement && document.activeElement.classList.contains('cd__freq-option')) { e.preventDefault(); document.activeElement.click(); }
          } else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeAllLists(); btn.focus(); }
        });
      });

      // Close dropdowns on outside click — dedupe the listener across refreshes.
      if (this._closeFreqLists) document.removeEventListener('click', this._closeFreqLists);
      this._closeFreqLists = closeAllLists;
      document.addEventListener('click', closeAllLists);
    },

    // Swap the drawer body/footer/count from a rendered cart-drawer section HTML
    // string. Shared by refresh() (GET section API) and by mutations that request
    // the section inline via the cart AJAX `sections` param (race-free).
    applyDrawerHtml: function(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newBody = doc.querySelector('[data-cart-body]');
      const curBody = document.querySelector('[data-cart-body]');
      if (newBody && curBody) curBody.innerHTML = newBody.innerHTML;

      const newFooter = doc.querySelector('[data-cart-footer]');
      const curFooter = document.querySelector('.cd__footer');
      if (newFooter && curFooter) {
        curFooter.innerHTML = newFooter.innerHTML;
        curFooter.style.display = '';
      } else if (!newFooter && curFooter) {
        curFooter.style.display = 'none';
      } else if (newFooter && !curFooter) {
        const f = document.createElement('footer');
        f.className = 'cd__footer';
        f.setAttribute('data-cart-footer', '');
        f.innerHTML = newFooter.innerHTML;
        this.drawer.appendChild(f);
      }

      const newCount = doc.querySelector('[data-cart-count]');
      if (newCount) {
        const text = newCount.textContent;
        document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = text);
      }

      this.initFrequencyToggles();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer-refresh' } }));
    },

    refresh: async function() {
      try {
        const response = await fetch('/?section_id=cart-drawer&_=' + Date.now(), { cache: 'no-store' });
        if (!response.ok) return;
        this.applyDrawerHtml(await response.text());
      } catch (e) { console.error('Cart refresh failed:', e); }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartDrawer.init());
  } else {
    CartDrawer.init();
  }

  window.FullSend = window.FullSend || {};
  window.FullSend.CartDrawer = CartDrawer;
})();
